import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import { bookingShape } from 'constants/propTypes';
import { useQuery } from '@apollo/client';
import { getHotelsStatuses, getHotelsInfo } from 'utils/booking';
import { Row, Column } from 'components/shared/Layout';
import Modal from 'components/ui/Modal';
import { FormattedMessage } from 'react-intl';
import { GET_HOTEL_BOOKINGS_INFO, HOTEL_BOOKING } from 'store/queries';
import { GridTable, GridHeaderCell, GridCell, GridCheckBox, UpdateButton } from './HotelStatusConflictWarning.styles';
import { useResolveExtrasConflicts } from './useResolveExtrasConflict';
import { buildHotelInfoFromDotwBooking } from '../../../utils/hotel';

const HotelStatusConflictWarning = ({ booking, refetch }) => {
  const [isOpen, toggleOpen] = useState(false);

  const closeModal = () => toggleOpen(false);
  const isDotwBooking = booking.hotels?.[0]?.isDotwBooking;

  const { data } = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKINGS_INFO, {
    variables: isDotwBooking ? { reference: booking.reference } : { bookingReference: booking.reference },
    fetchPolicy: 'network-only'
  });

  const { setUpdateExtra, updateAllExtras, isLoading, selectedExtras, selectedExtrasLength } = useResolveExtrasConflicts({
    refetchBooking: refetch,
    booking: booking,
    hotelBooking: isDotwBooking ? buildHotelInfoFromDotwBooking(data?.hotelBooking) : data?.getHotelBookings[0]
  });

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      updateAllExtras();
    },
    [updateAllExtras]
  );

  const hotelStatuses =
    (data?.getHotelBookings || data?.hotelBooking) &&
    getHotelsStatuses(booking.hotels, isDotwBooking ? data.hotelBooking : data.getHotelBookings, isDotwBooking);
  const hotelInfo =
    (data?.getHotelBookings || data?.hotelBooking) &&
    getHotelsInfo(
      booking.hotels,
      isDotwBooking ? [buildHotelInfoFromDotwBooking(data.hotelBooking)] : data.getHotelBookings,
      isDotwBooking
    );

  const hotelWithStatusConflict = hotelStatuses && hotelStatuses.find((h) => h.s3Status !== h.jacTravelStatus);
  const hotelWithInfoConflict =
    hotelInfo &&
    hotelInfo.find(
      (h) =>
        (h.s3CheckInDate && h.jacTravelCheckInDate && h.s3CheckInDate !== h.jacTravelCheckInDate) ||
        (h.s3GuestCount && h.jacTravelGuestCount && h.s3GuestCount !== h.jacTravelGuestCount) ||
        (h.s3HotelPrice && h.jacTravelHotelPrice && h.s3HotelPrice !== h.jacTravelHotelPrice) ||
        (h.s3NumberOfNights && h.jacTravelNumberOfNights && h.s3NumberOfNights !== h.jacTravelNumberOfNights)
    );

  useEffect(() => {
    const statuses =
      (data?.getHotelBookings || data?.hotelBooking) &&
      getHotelsStatuses(booking.hotels, isDotwBooking ? data.hotelBooking : data.getHotelBookings, isDotwBooking);
    const info =
      (data?.getHotelBookings || data?.hotelBooking) &&
      getHotelsInfo(
        booking.hotels,
        isDotwBooking ? [buildHotelInfoFromDotwBooking(data.hotelBooking)] : data.getHotelBookings,
        isDotwBooking
      );
    // if at least one hotel status in jac travel matches to one hotel status in s3 we assume all hotels statuses match
    // this is because we can not tell what s3 hotel a particular jac travel hotel data corresponds to
    const hasHotelWithStatusConflict = statuses?.length && !statuses.find((h) => h.s3Status === h.jacTravelStatus);
    const hasHotelWithInfoConflict =
      info?.length &&
      info.every(
        (h) =>
          (h.s3CheckInDate && h.jacTravelCheckInDate && h.s3CheckInDate !== h.jacTravelCheckInDate) ||
          (h.s3GuestCount && h.jacTravelGuestCount && h.s3GuestCount !== h.jacTravelGuestCount) ||
          (h.s3HotelPrice && h.jacTravelHotelPrice && h.s3HotelPrice !== h.jacTravelHotelPrice) ||
          (h.s3NumberOfNights && h.jacTravelNumberOfNights && h.s3NumberOfNights !== h.jacTravelNumberOfNights)
      );

    if (hasHotelWithStatusConflict || hasHotelWithInfoConflict) {
      toggleOpen(true);
      Sentry.captureMessage(`Hotel data in S3 and Jac Travel doesn't match for booking: ${booking.reference}`, {
        contexts: {
          'Hotel conflict data': hotelWithStatusConflict || hotelWithInfoConflict
        }
      });
    } else {
      toggleOpen(false);
    }
  }, [booking.hotels, data, toggleOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal isOpen={isOpen} title={<FormattedMessage id="manageBooking.hotelStatusConflict" />} onClose={closeModal}>
      <>
        <form onSubmit={onSubmit}>
          {hotelWithStatusConflict && (
            <Row key={hotelWithStatusConflict.reference} data-testid="hotel-status-mismatch-details">
              <Column>
                <h4>
                  <FormattedMessage id="manageBooking.hotelWithReference" values={{ reference: hotelWithStatusConflict.reference }} />:
                </h4>
              </Column>
              <Column>
                <strong>JacTravel: </strong>
                {hotelWithStatusConflict.jacTravelStatus}
              </Column>
              <Column>
                <strong>S3: </strong>
                {hotelWithStatusConflict.s3Status}
              </Column>
            </Row>
          )}
          {hotelWithInfoConflict?.reference && (
            <Row data-testid="hotel-info-mismatch-details">
              <Column>
                <h4>
                  <FormattedMessage id="manageBooking.hotelWithReference" values={{ reference: hotelWithInfoConflict.reference }} />:
                </h4>
              </Column>
              <GridTable rows={4}>
                <>
                  <GridHeaderCell />
                  <GridHeaderCell>JacTravel</GridHeaderCell>
                  <GridHeaderCell>S3</GridHeaderCell>
                  <GridHeaderCell>
                    <FormattedMessage id="manageBooking.correctInTheJacTravel" />
                  </GridHeaderCell>
                </>
                {hotelWithInfoConflict.s3HotelPrice &&
                  hotelWithInfoConflict.jacTravelHotelPrice &&
                  hotelWithInfoConflict.jacTravelHotelPrice !== hotelWithInfoConflict.s3HotelPrice && (
                    <>
                      <GridCell>
                        <strong>
                          <FormattedMessage id="extras.hotel.modal.price" />
                        </strong>
                      </GridCell>
                      <GridCell>{hotelWithInfoConflict.jacTravelHotelPrice}</GridCell>
                      <GridCell>{hotelWithInfoConflict.s3HotelPrice}</GridCell>
                      <GridCell />
                    </>
                  )}
                {hotelWithInfoConflict.s3CheckInDate &&
                  hotelWithInfoConflict.jacTravelCheckInDate &&
                  hotelWithInfoConflict.jacTravelCheckInDate !== hotelWithInfoConflict.s3CheckInDate && (
                    <>
                      <GridCell>
                        <strong>
                          <FormattedMessage id="extras.hotel.modal.checkInDate" />
                        </strong>
                      </GridCell>
                      <GridCell>
                        {hotelWithInfoConflict.jacTravelCheckInDate &&
                          moment(hotelWithInfoConflict.jacTravelCheckInDate).format('Do MMM YYYY')}
                      </GridCell>
                      <GridCell>
                        {hotelWithInfoConflict.s3CheckInDate && moment(hotelWithInfoConflict.s3CheckInDate).format('Do MMM YYYY')}
                      </GridCell>
                      <GridCell />
                    </>
                  )}
                {hotelWithInfoConflict.s3GuestCount &&
                  hotelWithInfoConflict.jacTravelGuestCount &&
                  hotelWithInfoConflict.jacTravelGuestCount !== hotelWithInfoConflict.s3GuestCount && (
                    <>
                      <GridCell>
                        <strong>
                          <FormattedMessage id="extras.hotel.modal.guestCount" />
                        </strong>
                      </GridCell>
                      <GridCell>{hotelWithInfoConflict.jacTravelGuestCount}</GridCell>
                      <GridCell>{hotelWithInfoConflict.s3GuestCount}</GridCell>
                      <GridCell>
                        <GridCheckBox
                          checked={!!selectedExtras['guestCount']}
                          onChange={(checked) => setUpdateExtra('guestCount', checked)}
                        />
                      </GridCell>
                    </>
                  )}
                {hotelWithInfoConflict.s3NumberOfNights &&
                  hotelWithInfoConflict.jacTravelNumberOfNights &&
                  hotelWithInfoConflict.jacTravelNumberOfNights !== hotelWithInfoConflict.s3NumberOfNights && (
                    <>
                      <GridCell>
                        <strong>
                          <FormattedMessage id="extras.hotel.modal.numberOfNights" />
                        </strong>
                      </GridCell>
                      <GridCell>{hotelWithInfoConflict.jacTravelNumberOfNights}</GridCell>
                      <GridCell>{hotelWithInfoConflict.s3NumberOfNights}</GridCell>
                      <GridCell>
                        <GridCheckBox
                          checked={!!selectedExtras['numberOfNights']}
                          onChange={(checked) => setUpdateExtra('numberOfNights', checked)}
                        />
                      </GridCell>
                    </>
                  )}
              </GridTable>
            </Row>
          )}
          <UpdateButton type="submit" loading={isLoading} disabled={selectedExtrasLength === 0 || isLoading}>
            Update
          </UpdateButton>
        </form>
      </>
    </Modal>
  );
};

HotelStatusConflictWarning.propTypes = {
  booking: bookingShape,
  refetch: PropTypes.func.isRequired
};

export default HotelStatusConflictWarning;
